import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Toast from '../../components/common/Toast';
import { createTeam } from '../../services/teamService';
import './Teams.css';

/**
 * Validation rules per R1.2 / R9.2:
 * - team_id: ^[a-z0-9][a-z0-9_-]{1,62}[a-z0-9]$ (DNS-prefix-safe)
 * - name: 1-100 chars after trim
 * - description: ≤500 chars
 * - contact_email: RFC-5322-shaped
 * - source_account_id: exactly 12 numeric digits
 */

const TEAM_ID_REGEX = /^[a-z0-9][a-z0-9_-]{1,62}[a-z0-9]$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ACCOUNT_ID_REGEX = /^\d{12}$/;

function validateField(name, value) {
  const v = (value || '').trim();
  switch (name) {
    case 'team_id':
      if (!v) return 'Team ID is required';
      if (!TEAM_ID_REGEX.test(v)) return 'Must be 3-64 lowercase chars: a-z, 0-9, hyphens, underscores. Must start and end with a letter or digit.';
      return null;
    case 'name':
      if (!v) return 'Name is required';
      if (v.length > 100) return 'Name must be 100 characters or fewer';
      return null;
    case 'description':
      if (v.length > 500) return 'Description must be 500 characters or fewer';
      return null;
    case 'contact_email':
      if (!v) return 'Contact email is required';
      if (!EMAIL_REGEX.test(v)) return 'Enter a valid email address';
      return null;
    case 'source_account_id':
      if (v && !ACCOUNT_ID_REGEX.test(v)) return 'Must be exactly 12 digits';
      return null;
    default:
      return null;
  }
}

/**
 * TeamCreate Page (Task 12.2)
 *
 * Form with field-level validation matching R1.2 / R9.2.
 * Handles 409 name collision and 400 field errors from the API.
 */
export default function TeamCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    team_id: '',
    name: '',
    description: '',
    contact_email: '',
    source_account_id: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Clear error on edit if field was touched
    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateAll = () => {
    const newErrors = {};
    Object.keys(form).forEach(key => {
      const err = validateField(key, form[key]);
      if (err) newErrors[key] = err;
    });
    setErrors(newErrors);
    setTouched({ team_id: true, name: true, description: true, contact_email: true, source_account_id: true });
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) return;

    try {
      setSubmitting(true);
      await createTeam({
        team_id: form.team_id.trim(),
        name: form.name.trim(),
        description: form.description.trim(),
        source_account_id: form.source_account_id.trim(),
        contact_email: form.contact_email.trim()
      });
      setToast({ message: `Team "${form.name.trim()}" created successfully`, type: 'success' });
      setTimeout(() => navigate('/admin/teams'), 1500);
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;

      if (status === 409) {
        setErrors(prev => ({ ...prev, name: 'A team with this name already exists' }));
      } else if (status === 400 && data?.errors) {
        // Map field-level errors from backend
        const fieldErrors = {};
        Object.entries(data.errors).forEach(([field, msg]) => {
          fieldErrors[field] = msg;
        });
        setErrors(prev => ({ ...prev, ...fieldErrors }));
      } else {
        setToast({ message: data?.message || err.message || 'Failed to create team', type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hasError = (field) => touched[field] && errors[field];

  return (
    <div className="teams-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <button className="back-link" onClick={() => navigate('/admin/teams')}>
              <ArrowLeft size={20} />
              Back to Teams
            </button>
            <h1 className="page-title">
              <Shield size={32} />
              Create Team
            </h1>
            <p className="page-subtitle">Register a new source team for file ingest</p>
          </div>
        </div>
      </div>

      <Card className="teams-card team-form-card">
        <form onSubmit={handleSubmit} className="team-form" noValidate>
          <div className="form-group">
            <label htmlFor="team_id">Team ID</label>
            <input
              id="team_id"
              name="team_id"
              type="text"
              value={form.team_id}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g. dms, analytics-prod"
              className={hasError('team_id') ? 'input-error' : ''}
              disabled={submitting}
              autoComplete="off"
            />
            <p className="field-hint">Lowercase, 3-64 chars. Letters, digits, hyphens, underscores. Cannot be changed after creation.</p>
            {hasError('team_id') && <p className="field-error">{errors.team_id}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="name">Display Name</label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g. Data Migration Service"
              className={hasError('name') ? 'input-error' : ''}
              disabled={submitting}
            />
            {hasError('name') && <p className="field-error">{errors.name}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description <span className="optional-label">(optional)</span></label>
            <textarea
              id="description"
              name="description"
              value={form.description}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Brief description of the team and its data flows"
              className={hasError('description') ? 'input-error' : ''}
              disabled={submitting}
              rows={3}
            />
            <p className="field-hint">{form.description.length}/500</p>
            {hasError('description') && <p className="field-error">{errors.description}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="source_account_id">Source AWS Account ID <span className="optional-label">(optional)</span></label>
            <input
              id="source_account_id"
              name="source_account_id"
              type="text"
              value={form.source_account_id}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="123456789012"
              className={hasError('source_account_id') ? 'input-error' : ''}
              disabled={submitting}
              maxLength={12}
              autoComplete="off"
            />
            <p className="field-hint">The 12-digit AWS account that will push files into the shared trigger bucket.</p>
            {hasError('source_account_id') && <p className="field-error">{errors.source_account_id}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="contact_email">Contact Email</label>
            <input
              id="contact_email"
              name="contact_email"
              type="email"
              value={form.contact_email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="team-lead@example.com"
              className={hasError('contact_email') ? 'input-error' : ''}
              disabled={submitting}
            />
            {hasError('contact_email') && <p className="field-error">{errors.contact_email}</p>}
          </div>

          <div className="form-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/admin/teams')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Team'}
            </Button>
          </div>
        </form>
      </Card>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
    </div>
  );
}
